import { z } from "zod";

import { ReviewQuestionModel } from "@performyard/models";

export const MAX_NAME_LENGTH = 250;
export const MAX_QUESTION_LENGTH = 3000;
export const MAX_SUBTEXT_LENGTH = 12000;

//export type ReviewQuestion = PatchCompanyReviewFormRequestJson["questions"][0];
export const ReviewQuestionSchema = ReviewQuestionModel.omit({ _id: true, company: true })
	.extend({
		refId: z.string(),
		questions: z
			.array(
				z.object({
					refId: z.string(),
					weight: z
						.number({ message: "This is a required field" })
						.gt(0, { message: "Value must be greater than 0" }),
				})
			)
			.optional(),
		min: z.number().nullable().optional(),
		max: z.number().nullable().optional(),
		scoring_min: z.number().nullable().optional(),
		scoring_max: z.number().nullable().optional(),
	})
	.superRefine((data, ctx) => {
		if (data.type == "numeric" || data.type == "goal-discussion") {
			// Only validate min/max comparison when both values are numbers
			if (typeof data.min === "number" && typeof data.max === "number") {
				if (data.min >= data.max) {
					ctx.addIssue({
						code: z.ZodIssueCode.custom,
						message: "Min must be less than Max",
						path: ["min"],
					});
				}
			}

			// Only validate scoring_min/scoring_max comparison when both values are numbers
			// For goal-discussion questions, only validate when scoring is enabled
			if (
				typeof data.scoring_min === "number" &&
				typeof data.scoring_max === "number" &&
				!(data.type === "goal-discussion" && !data.add_scoring)
			) {
				if (data.scoring_min >= data.scoring_max) {
					ctx.addIssue({
						code: z.ZodIssueCode.custom,
						message: "Min must be less than Max",
						path: ["scoring_min"],
					});
				}
			}
		}
	})
	.refine(
		(data) => {
			if (data.type == "calculator") {
				if (!data?.aggregation) {
					return false;
				}
			}
			return true;
		},
		{
			message: "This is a required field",
			path: ["aggregation"],
		}
	)
	.superRefine((data, ctx) => {
		if (data.type === "calculator") {
			if (!data.questions || data.questions.length === 0) {
				ctx.addIssue({
					code: z.ZodIssueCode.custom,
					message:
						"You do not have any questions selected for this calculation. Please edit or remove this question type in order to continue.",
					path: ["questions"],
				});
			}
		}
	})
	.superRefine((question, ctx) => {
		if (question.type == "goal-discussion") {
			if (question?.type === "goal-discussion" && question?.add_scaling) {
				// Check each scaling choice and add issues for invalid ones
				for (let i = 0; i < question.add_scaling.length; i++) {
					const choice = question.add_scaling[i];
					if (!choice?.name.trim()) {
						ctx.addIssue({
							code: z.ZodIssueCode.custom,
							message: "This is a required field",
							path: ["add_scaling", i, "name"],
						});
					}
				}
			}

			if (question.add_scoring) {
				// check if either scoring_min OR scoring_max are null or undefined
				if (typeof question.scoring_min === "number" && typeof question.scoring_max === "number") {
					if (question.scoring_min >= question.scoring_max) {
						ctx.addIssue({
							code: z.ZodIssueCode.custom,
							message: "Min must be less than Max",
							path: ["scoring_min"],
						});
					}
				}
			}
		}
	})
	.superRefine((data, ctx) => {
		// require either question or subtext but not both.
		if (!(data.question && data.question.trim().length > 0) && !(data.subtext && data.subtext.trim().length > 0)) {
			ctx.addIssue({
				code: z.ZodIssueCode.custom,
				message: "Title or subtitle is required",
				path: ["question"],
			});
		}
	});

export const ReviewQuestionsSchema = z.object({
	questions: z.array(ReviewQuestionSchema),
});

export type ReviewQuestion = z.infer<typeof ReviewQuestionSchema>;
export type ReviewQuestions = z.infer<typeof ReviewQuestionsSchema>;

export const QUESTION_TYPES_CAN_BE_REQUIRED: ReviewQuestion["type"][] = [
	"text",
	"numeric",
	"rating-scale",
	"multiple-choice",
	"multiple-select",
	"multiline",
	"date",
	"goal-discussion",
	"goal-creation",
];
const ALLOWED_CALCULATOR_QUESTION_TYPES: ReviewQuestion["type"][] = ["numeric", "rating-scale", "calculator"];
const QUESTION_TYPES_TITLE_OR_SUBTITLE_NOT_REQUIRED: ReviewQuestion["type"][] = ["instructions"];

export const hasAllowedCalculatorTypes = (question: ReviewQuestion) => {
	if (ALLOWED_CALCULATOR_QUESTION_TYPES.includes(question.type)) {
		return true;
	}

	// if goal discussion question, allow types is true if it also has numeric or rating scale
	if (question.type === "goal-discussion") {
		return question.add_scoring || question.add_scaling?.length;
	}
	return false;
};

/**
 * Validates basic fields common to all question types
 */
export const validateBasicFields = (question: ReviewQuestion) => {
	const errors = {};

	// Validate question text
	if (!QUESTION_TYPES_TITLE_OR_SUBTITLE_NOT_REQUIRED.includes(question.type) && !question.question.trim()) {
		errors["question"] = "This is a required field";
	} else if (question.question.trim().length > MAX_QUESTION_LENGTH) {
		errors["question"] = `Question has maximum length of ${MAX_QUESTION_LENGTH} characters`;
	}

	// Validate subtext if present
	if (
		!QUESTION_TYPES_TITLE_OR_SUBTITLE_NOT_REQUIRED.includes(question.type) &&
		question?.subtext &&
		question.subtext?.trim()?.length > MAX_SUBTEXT_LENGTH
	) {
		errors["subtext"] = `Subtitle has maximum length of ${MAX_SUBTEXT_LENGTH} characters`;
	}

	return errors;
};

export const normalizeQuestions = (questions: ReviewQuestion[]) => {
	const questionsMap = new Map(questions.map((q) => [q.refId, q]));

	return questions.map((question) => {
		if (question.questions && question.type == "calculator") {
			// filter out any subquestions that are not in the questions map
			question.questions = question.questions.filter((q) => questionsMap.has(q.refId));
		} else if (question.questions) {
			// remove subquestions array if it is not a calculator question
			// this is to ensure that the questions array is not sent to the server if it is not a calculator question
			delete question.questions;
		}

		let scoringMin = question.scoring_min;
		let scoringMax = question.scoring_max;

		// For goal-discussion questions, if scoring is disabled, ensure scoring_min/max are undefined
		if (question.type === "goal-discussion" && !question.add_scoring) {
			scoringMin = undefined;
			scoringMax = undefined;
		}

		return {
			...question,
			min: question.min === null ? undefined : question.min,
			max: question.max === null ? undefined : question.max,
			scoring_min: scoringMin === null ? undefined : scoringMin,
			scoring_max: scoringMax === null ? undefined : scoringMax,
		};
	});
};
